import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { FilterSegment } from 'pages/main/analytics/parts/AnalyticsFilterBar'
import useAnalyticsOptions from 'pages/main/analytics/parts/useAnalyticsOptions'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import ChainFilterBar, { useChainFilters, FilterField, FilterSelect, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	money, percent, columnStats, heatStyle, intensityStyle, buildRanks,
	RankBadge, Delta, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty, TableSearch,
} from '../parts/chainUi'

// Группы статусов документа: posted — принят получателем, pending — ждёт приёмки,
// cancelled — удалён / отменён / отклонён (legacy -1)
const STATUS_GROUPS = ['posted', 'pending', 'cancelled']

// Цвет группы — как у статуса в справочнике document_statuses
const GROUP_COLOR = { posted: 'success', pending: 'secondary', cancelled: 'danger' }

// Цвета справочника статусов (bootstrap-имя) → rgb для заливки ячейки на любой теме
const STATUS_RGB = {
	success: '18, 184, 134',
	danger: '240, 101, 72',
	warning: '245, 159, 0',
	secondary: '134, 142, 150',
	info: '41, 156, 219',
	primary: '91, 115, 232',
	dark: '108, 117, 125',
}

// Статус документа (document_statuses.value) → ключ перевода
const STATUS_LABEL_KEYS = {
	posted: 'chain_tr_status_posted',
	pending: 'chain_tr_status_pending',
	cancelled: 'cancelled',
	deleted: 'deleted',
	rejected: 'chain_tr_status_rejected',
}

function statusCellStyle(color) {
	const rgb = STATUS_RGB[color] || STATUS_RGB.secondary
	return { background: `rgba(${rgb}, 0.18)`, textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' }
}

function qty(value) {
	const number = Number(value || 0)
	if (Number.isInteger(number)) return formatMoney(number)
	return formatMoney(number, 3).replace(/\.?0+$/, '')
}

function monthLabel(month) {
	return month.slice(5, 7) + '.' + month.slice(0, 4)
}

/**
 * Перемещения в другой логин: мои филиалы-отправители → точки других логинов
 * (доступ через pos_transfer_access). Две вкладки — по товарам и по суммам.
 * defaultTab — с какой вкладки открыть (старый роут «по суммам» открывает вторую).
 */
function Transfers({ defaultTab = 'products' }) {
	const { t } = useTranslation()
	const [tab, setTab] = useState(defaultTab)
	const [summary, setSummary] = useState(null)
	const [productsData, setProductsData] = useState(null)
	const [recipientOptions, setRecipientOptions] = useState([])
	const [cellMode, setCellMode] = useState('quantity')
	const [search, setSearch] = useState('')
	const [details, setDetails] = useState(null)
	const [openedDoc, setOpenedDoc] = useState(null)

	const filters = useChainFilters({ to_pos_ids: [], statuses: ['posted', 'pending'] }, searchWithFilter)
	const { draft, patch, applied } = filters
	const options = useAnalyticsOptions([])

	const statusLabels = useMemo(() => ({
		posted: t('chain_tr_status_posted'),
		pending: t('chain_tr_status_pending'),
		cancelled: t('chain_tr_status_cancelled'),
	}), [t])
	const statusOptions = useMemo(() => STATUS_GROUPS.map(id => ({ id, name: statusLabels[id] })), [statusLabels])
	const typeLabels = useMemo(() => ({ 0: t('element'), 1: t('product'), 2: t('dish_preparation'), 3: t('modifiers') }), [t])

	useEffect(() => {
		GET('/chain/report/transfer-recipients').then(response => {
			if (httpOk(response)) setRecipientOptions(response.data || [])
		})
	}, [])

	async function fetchSummary(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/transfers', filter), {}, { loader: false })
		if (!httpOk(response)) return null
		const result = { ...response.data, key: JSON.stringify(filter) }
		setSummary(result)
		return result
	}

	async function fetchProducts(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/transfers-products', filter), {}, { loader: false })
		if (!httpOk(response)) return null
		const result = { ...response.data, key: JSON.stringify(filter) }
		setProductsData(result)
		return result
	}

	// Грузим только открытую вкладку; вторая подтянется при переключении
	function searchWithFilter(filter) {
		return tab === 'products' ? fetchProducts(filter) : fetchSummary(filter)
	}

	function changeTab(id) {
		setTab(id)
		const key = JSON.stringify(applied)
		if (id === 'products' && productsData?.key !== key) fetchProducts(applied)
		if (id === 'amounts' && summary?.key !== key) fetchSummary(applied)
	}

	const senderCount = (applied.pos_ids || []).length
	const recipientCount = (applied.to_pos_ids || []).length
	const subtitle = <>
		{senderCount === 0 ? t('chain_all_branches') : `${t('chain_tr_senders')}: ${senderCount}`}
		{' → '}
		{recipientCount === 0 ? t('chain_tr_all_recipients') : `${t('chain_tr_recipients')}: ${recipientCount}`}
	</>

	async function openDetails({ toPosId = null, toPosName = null, product = null, month = null, status = null }) {
		const extra = { to_pos_id: toPosId, product_id: product ? product.product_id : null, month, status }
		const response = await GET(buildAnalyticsUrl('/chain/report/transfers-details', applied, extra), {}, { loader: true })
		if (httpOk(response)) {
			const parts = [toPosName || t('chain_tr_all_recipients')]
			if (product) parts.unshift(product.product_name)
			if (month) parts.push(monthLabel(month))
			if (status) parts.push(statusLabels[status])
			setOpenedDoc(null)
			setDetails({ ...response.data, title: parts.join(' — '), product })
		}
	}

	async function openDocument(id) {
		const response = await GET(`/chain/report/transfers-document?id=${id}`, {}, { loader: true })
		if (httpOk(response)) setOpenedDoc(response.data)
	}

	function closeModal() {
		setDetails(null)
		setOpenedDoc(null)
	}

	function documentTitle(row) {
		return row.document_number ? `№ ${row.document_number}` : `#${row.id}`
	}

	function rowStatusLabel(row) {
		return t(STATUS_LABEL_KEYS[row.status_value] || STATUS_LABEL_KEYS[row.status_key] || 'status')
	}

	async function exportExcel() {
		const key = JSON.stringify(applied)
		const summaryData = summary?.key === key ? summary : await fetchSummary(applied)
		const productData = productsData?.key === key ? productsData : await fetchProducts(applied)
		if (!summaryData || !productData) return

		const period = `${t('period')}: ${periodText(summaryData.start_date, summaryData.end_date)}`
		const recipients = summaryData.recipients || []
		const productRecipients = productData.recipients || []
		const productColumns = (valueType) => [
			{ label: t('chain_wo_position'), type: 'text', width: 34 },
			{ label: t('unit'), type: 'text', width: 8 },
			{ label: t('category'), type: 'text', width: 18 },
			...productRecipients.map(recipient => ({ label: recipient.to_pos_name, type: valueType })),
			{ label: t('total'), type: valueType },
		]
		const productInfo = (product) => [product.product_name, product.unit_name || '', product.category_name || '']
		const rankByAmount = buildRanks(recipients, 'amount', true, 'to_pos_id').ranks
		const totals = summaryData.totals || {}
		const statuses = summaryData.statuses || {}

		downloadExcel(`${t('chain_tr_title')} ${periodText(summaryData.start_date, summaryData.end_date)}`, [
			{
				name: t('chain_tr_recipients'),
				title: t('chain_tr_title'),
				subtitle: period,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 8 },
					{ label: t('chain_tr_recipient'), type: 'text', width: 30 },
					{ label: t('chain_tr_login'), type: 'text', width: 18 },
					{ label: t('chain_tr_amount'), type: 'money' },
					{ label: t('share'), type: 'percent', width: 9 },
					{ label: t('chain_prev_period'), type: 'money' },
					{ label: t('chain_growth'), type: 'percent', width: 9 },
					{ label: t('cost_price'), type: 'money' },
					{ label: t('markup'), type: 'money' },
					{ label: t('chain_tr_markup_percent'), type: 'percent', width: 10 },
					{ label: t('chain_tr_documents'), type: 'int', width: 10 },
					{ label: t('chain_tr_avg_document'), type: 'money' },
					...STATUS_GROUPS.map(status => ({ label: statusLabels[status], type: 'money' })),
				],
				rows: recipients.map(item => [
					rankByAmount[item.to_pos_id], item.to_pos_name, item.to_login || '', item.amount, item.share, item.prev_amount,
					item.amount_delta, item.cost, item.markup, item.markup_percent, item.documents, item.avg_document,
					...STATUS_GROUPS.map(status => item.statuses?.[status]?.amount || 0),
				]),
				footer: ['', t('total'), '', totals.amount, 100, totals.prev_amount, totals.amount_delta, totals.cost, totals.markup,
					totals.markup_percent, totals.documents, totals.avg_document, ...STATUS_GROUPS.map(status => statuses[status]?.amount || 0)],
			},
			{
				name: t('chain_by_months'),
				title: `${t('chain_tr_title')} — ${t('chain_by_months')}`,
				subtitle: period,
				columns: [
					{ label: t('chain_tr_recipient'), type: 'text', width: 30 },
					...(summaryData.months || []).map(month => ({ label: monthLabel(month), type: 'money' })),
					{ label: t('total'), type: 'money' },
				],
				rows: recipients.map(item => [item.to_pos_name, ...item.months, item.amount]),
				footer: [t('total'), ...(summaryData.month_totals || []), totals.amount],
			},
			{
				name: `${t('chain_tr_tab_products')} — ${t('quantity')}`,
				title: `${t('chain_tr_title')} — ${t('quantity')}`,
				subtitle: period,
				columns: productColumns('number'),
				rows: (productData.products || []).map(product => [
					...productInfo(product),
					...productRecipients.map(recipient => Number(product.quantities?.[recipient.to_pos_id] || 0)),
					product.quantity,
				]),
			},
			{
				name: `${t('chain_tr_tab_products')} — ${t('amount')}`,
				title: `${t('chain_tr_title')} — ${t('amount')}`,
				subtitle: period,
				columns: [...productColumns('money'), { label: t('cost_price'), type: 'money' }, { label: t('share'), type: 'percent', width: 9 }],
				rows: (productData.products || []).map(product => [
					...productInfo(product),
					...productRecipients.map(recipient => Number(product.amounts?.[recipient.to_pos_id] || 0)),
					product.amount,
					product.cost,
					product.share,
				]),
				footer: [t('total'), '', '', ...productRecipients.map(recipient => recipient.amount), productData.totals?.amount, productData.totals?.cost, 100],
			},
		])
	}

	function exportDetails() {
		if (openedDoc) {
			const doc = openedDoc.document
			downloadExcel(`${t('chain_tr_document')} ${documentTitle(doc)}`, [{
				name: t('chain_tr_document'),
				title: `${t('chain_tr_document')} ${documentTitle(doc)}: ${doc.from_pos_name} → ${doc.to_pos_name}`,
				subtitle: `${formatDate(doc.created_at, 'dd.MM.yyyy HH:mm')} · ${rowStatusLabel(doc)}`,
				columns: [
					{ label: t('chain_wo_position'), type: 'text', width: 34 },
					{ label: t('quantity'), type: 'number', width: 10 },
					{ label: t('unit'), type: 'text', width: 8 },
					{ label: t('cost_price'), type: 'money' },
					{ label: t('chain_tr_price'), type: 'money' },
					{ label: t('chain_tr_cost_amount'), type: 'money' },
					{ label: t('amount'), type: 'money' },
				],
				rows: openedDoc.lines.map(line => [line.product_name, line.quantity, line.unit_name || '', line.cost_price, line.sale_price, line.cost, line.amount]),
				footer: [t('total'), '', '', '', '', doc.cost, doc.amount],
			}])
			return
		}
		if (!details) return
		const withProduct = !!details.product
		downloadExcel(`${t('chain_tr_title')} — ${details.title}`, [{
			name: t('chain_tr_documents'),
			title: details.title,
			subtitle: `${t('period')}: ${periodText(details.start_date, details.end_date)}`,
			columns: [
				{ label: t('date'), type: 'text', width: 18 },
				{ label: t('number'), type: 'text', width: 10 },
				{ label: t('chain_tr_sender'), type: 'text', width: 26 },
				{ label: t('chain_tr_recipient'), type: 'text', width: 26 },
				{ label: t('status'), type: 'text', width: 14 },
				{ label: t('created_by'), type: 'text', width: 20 },
				{ label: t('note'), type: 'text', width: 30 },
				...(withProduct ? [{ label: t('quantity'), type: 'number', width: 10 }, { label: details.product.product_name, type: 'money' }] : []),
				{ label: t('cost_price'), type: 'money' },
				{ label: t('chain_tr_amount'), type: 'money' },
			],
			rows: details.rows.map(row => [
				formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), documentTitle(row),
				[row.from_pos_name, row.from_wms_name].filter(Boolean).join(' · '),
				[row.to_pos_name, row.to_wms_name].filter(Boolean).join(' · '),
				rowStatusLabel(row), row.created_by_name || row.created_by || '', row.note || '',
				...(withProduct ? [row.product_quantity, row.product_amount] : []),
				row.cost, row.amount,
			]),
			footer: [t('total'), '', '', '', '', '', '', ...(withProduct ? [details.product_quantity, details.product_total] : []), details.total_cost, details.total],
		}])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_tr_title')}
				icon="uil-truck"
				subtitle={subtitle}
				filters={filters}
				branches={false}
				wide
				onExcel={exportExcel}
				tabs={[
					{ id: 'products', label: t('chain_tr_tab_products'), icon: 'uil-box' },
					{ id: 'amounts', label: t('chain_tr_tab_amounts'), icon: 'uil-money-bill' },
				]}
				activeTab={tab}
				onTabChange={changeTab}
			>
				<FilterField label={t('chain_tr_senders')}>
					<FilterSelect
						isMulti
						options={options.poses}
						allLabel={t('chain_all_branches')}
						value={draft.pos_ids}
						getOptionLabel={(option) => option.short_name || option.name}
						onChange={(ids) => patch({ pos_ids: ids })}
					/>
				</FilterField>
				<FilterField label={t('chain_tr_recipients')} wide>
					<FilterSelect isMulti options={recipientOptions} allLabel={t('chain_tr_all_recipients')} value={draft.to_pos_ids} onChange={(ids) => patch({ to_pos_ids: ids })} />
				</FilterField>
				<FilterField label={t('status')}>
					<FilterSelect isMulti options={statusOptions} allLabel={t('chain_tr_all_statuses')} value={draft.statuses} onChange={(ids) => patch({ statuses: ids })} />
				</FilterField>
			</ChainFilterBar>

			{tab === 'products'
				? <ProductsTab data={productsData} cellMode={cellMode} onCellMode={setCellMode} search={search} onSearch={setSearch}
					typeLabels={typeLabels} openDetails={openDetails} />
				: <AmountsTab data={summary} statusLabels={statusLabels} openDetails={openDetails} />}

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={closeModal} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{openedDoc
							? <>{t('chain_tr_document')} {documentTitle(openedDoc.document)}: {openedDoc.document.from_pos_name} → {openedDoc.document.to_pos_name}</>
							: details?.title}
						<div className="chain-muted fw-normal">
							{openedDoc
								? <>{formatDate(openedDoc.document.created_at, 'dd.MM.yyyy HH:mm')} · {openedDoc.document.created_by_name || openedDoc.document.created_by || '—'} · {money(openedDoc.document.amount)}</>
								: <>{periodText(details?.start_date, details?.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_tr_documents_lower')} · {money(details?.total)}</>}
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{openedDoc ? (
						<>
							<div className="d-flex align-items-center flex-wrap gap-2 mb-2">
								<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpenedDoc(null)}>
									<i className="uil uil-arrow-left me-1"></i>{t('back')}
								</button>
								<span className="px-2 py-1 rounded" style={statusCellStyle(openedDoc.document.status_color || GROUP_COLOR[openedDoc.document.status_key])}>
									{rowStatusLabel(openedDoc.document)}
								</span>
								<span className="chain-muted">
									{openedDoc.document.from_wms_name || '—'} → {openedDoc.document.to_wms_name || '—'}
									{openedDoc.document.note && <> · <b>{t('note')}:</b> {openedDoc.document.note}</>}
								</span>
							</div>
							{Math.abs(Number(openedDoc.lines_amount) - Number(openedDoc.document.amount)) > 1 && (
								<div className="chain-note mb-2">
									<i className="uil uil-exclamation-triangle"></i>
									<span>{t('chain_tr_lines_mismatch')} ({money(openedDoc.lines_amount)} / {money(openedDoc.document.amount)})</span>
								</div>
							)}
							<table className="chain-table">
								<thead>
									<tr>
										<th>#</th>
										<th>{t('chain_wo_position')}</th>
										<th>{t('type')}</th>
										<th className="text-end">{t('quantity')}</th>
										<th className="text-end">{t('cost_price')}</th>
										<th className="text-end">{t('chain_tr_price')}</th>
										<th className="text-end">{t('chain_tr_cost_amount')}</th>
										<th className="text-end">{t('amount')}</th>
									</tr>
								</thead>
								<tbody>
									{openedDoc.lines.length === 0 && <ChainEmpty colSpan={8} />}
									{openedDoc.lines.map((line, index) => (
										<tr key={line.id} style={details?.product && details.product.product_id === line.product_id ? { background: 'rgba(91, 115, 232, 0.12)' } : undefined}>
											<td className="chain-index">{index + 1}</td>
											<td>{line.product_name}</td>
											<td>{typeLabels[line.product_type_id] || '—'}</td>
											<td className="chain-num">{qty(line.quantity)} <span className="chain-muted">{line.unit_name}</span></td>
											<td className="chain-num">{money(line.cost_price)}</td>
											<td className="chain-num">{money(line.sale_price)}</td>
											<td className="chain-num">{money(line.cost)}</td>
											<td className="chain-num"><b>{money(line.amount)}</b></td>
										</tr>
									))}
								</tbody>
								{openedDoc.lines.length > 0 && (
									<tfoot>
										<tr>
											<td></td>
											<td colSpan={5}>{t('total')}</td>
											<td className="chain-num">{money(openedDoc.document.cost)}</td>
											<td className="chain-num">{money(openedDoc.document.amount)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</>
					) : (
						<>
							{details?.limited && (
								<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
							)}
							<table className="chain-table">
								<thead>
									<tr>
										<th>#</th>
										<th>{t('date')}</th>
										<th>{t('number')}</th>
										<th>{t('chain_tr_sender')}</th>
										<th>{t('chain_tr_recipient')}</th>
										<th className="text-center">{t('status')}</th>
										<th>{t('created_by')}</th>
										<th>{t('note')}</th>
										{details?.product && <th className="text-end">{details.product.product_name}</th>}
										<th className="text-end">{t('cost_price')}</th>
										<th className="text-end">{t('chain_tr_amount')}</th>
									</tr>
								</thead>
								<tbody>
									{(details?.rows || []).length === 0 && <ChainEmpty colSpan={details?.product ? 11 : 10} />}
									{(details?.rows || []).map((row, index) => (
										<tr key={row.id} className="chain-click" onClick={() => openDocument(row.id)} title={t('chain_tr_open_document')}>
											<td className="chain-index">{index + 1}</td>
											<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
											<td className="text-nowrap">{documentTitle(row)}</td>
											<td>
												{row.from_pos_name}
												{row.from_wms_name && <div className="chain-muted">{row.from_wms_name}</div>}
											</td>
											<td>
												{row.to_pos_name}
												{row.to_wms_name && <div className="chain-muted">{row.to_wms_name}</div>}
											</td>
											<td style={statusCellStyle(row.status_color || GROUP_COLOR[row.status_key])}>{rowStatusLabel(row)}</td>
											<td>{row.created_by_name || row.created_by || '—'}</td>
											<td style={{ maxWidth: '220px' }}>{row.note || '—'}</td>
											{details?.product && (
												<td className="chain-num">
													<b>{money(row.product_amount)}</b>
													<div className="chain-muted">{qty(row.product_quantity)} {details.product.unit_name}</div>
												</td>
											)}
											<td className="chain-num">{money(row.cost)}</td>
											<td className="chain-num"><b>{money(row.amount)}</b></td>
										</tr>
									))}
								</tbody>
								{(details?.rows || []).length > 0 && (
									<tfoot>
										<tr>
											<td></td>
											<td colSpan={7}>{t('total')}</td>
											{details?.product && (
												<td className="chain-num">
													{money(details.product_total)}
													<div className="chain-muted">{qty(details.product_quantity)} {details.product.unit_name}</div>
												</td>
											)}
											<td className="chain-num">{money(details?.total_cost)}</td>
											<td className="chain-num">{money(details?.total)}</td>
										</tr>
									</tfoot>
								)}
							</table>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={openedDoc ? !openedDoc.lines.length : !details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={closeModal}>{t('close')}</button>
				</Modal.Footer>
			</Modal>
		</>
	)
}

// Вкладка «По товарам»: матрица позиция × получатель
function ProductsTab({ data, cellMode, onCellMode, search, onSearch, typeLabels, openDetails }) {
	const { t } = useTranslation()
	const recipients = useMemo(() => data?.recipients || [], [data])
	const totals = data?.totals || {}

	const products = useMemo(() => {
		const list = data?.products || []
		const query = search.trim().toLowerCase()
		if (!query) return list
		return list.filter(item => `${item.product_name} ${item.category_name || ''}`.toLowerCase().includes(query))
	}, [data, search])

	function cellValue(product, toPosId) {
		const source = cellMode === 'amount' ? product.amounts : product.quantities
		return Number(source?.[toPosId] || 0)
	}

	const readSortValue = (row, key) => key.startsWith('pos:') ? cellValue(row, Number(key.slice(4))) : row[key]
	const { sorted, sort, toggle } = useSort(products, 'amount', 'desc', readSortValue)
	const topProduct = (data?.products || [])[0]
	const topRecipient = recipients[0]
	const markupPercent = totals.cost > 0 ? totals.markup / totals.cost * 100 : null

	return (
		<>
			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_tr_amount')} icon="uil-truck" color="#5b73e8"
						value={money(totals.amount)}
						sub={<>{t('cost_price')} {money(totals.cost)}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('markup')} icon="uil-chart-growth" color="#12b886"
						value={money(totals.markup)}
						sub={<>{percent(markupPercent)} {t('chain_tr_to_cost')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_wo_positions')} icon="uil-box" color="#f59f00"
						value={formatMoney(totals.products)}
						sub={topRecipient ? <>{t('chain_tr_top_recipient')}: {topRecipient.to_pos_name}</> : null} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_tr_top_position')} icon="uil-star" color="#7c4dff"
						value={topProduct ? topProduct.product_name : '—'}
						sub={topProduct ? <>{money(topProduct.amount)} · {percent(topProduct.share)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_tr_matrix')}</h5>
							<FilterSegment
								options={[{ id: 'quantity', label: t('quantity') }, { id: 'amount', label: t('chain_mode_amount') }]}
								value={cellMode}
								onChange={onCellMode}
							/>
						</div>
						<div className="chain-toolbar-group">
							<HeatLegend mode="success" />
							<TableSearch value={search} onChange={onSearch} placeholder={t('chain_search_position')} />
						</div>
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="product_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_wo_position')}</SortTh>
									{recipients.map(recipient => (
										<SortTh key={recipient.to_pos_id} sortKey={`pos:${recipient.to_pos_id}`} sort={sort} onSort={toggle} className="text-end"
											title={recipient.to_login || undefined}>
											{recipient.to_pos_name}
										</SortTh>
									))}
									<SortTh sortKey={cellMode === 'amount' ? 'amount' : 'quantity'} sort={sort} onSort={toggle} className="text-end chain-total-col">{t('total')}</SortTh>
									<SortTh sortKey="share" sort={sort} onSort={toggle} className="text-end">{t('share')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={recipients.length + 4} />}
								{sorted.map((product, rowIndex) => {
									const rowMax = Math.max(0, ...recipients.map(recipient => cellValue(product, recipient.to_pos_id)))
									return (
										<tr key={product.product_id}>
											<td className="chain-index chain-sticky">{rowIndex + 1}</td>
											<td className="chain-name chain-sticky-2 chain-click" onClick={() => openDetails({ product })} title={t('chain_open_details')}>
												<span>{product.product_name}</span>
												<div className="chain-muted">
													{[product.unit_name, product.category_name, typeLabels[product.product_type_id]].filter(Boolean).join(' · ')}
												</div>
											</td>
											{recipients.map(recipient => {
												const value = cellValue(product, recipient.to_pos_id)
												const isMax = value > 0 && value === rowMax && recipients.length > 1
												return (
													<td key={recipient.to_pos_id}
														className={`chain-num ${value > 0 ? 'chain-click' : ''}`}
														style={intensityStyle(value, rowMax, 'success')}
														title={value > 0 ? `${recipient.to_pos_name}: ${qty(product.quantities?.[recipient.to_pos_id])} ${product.unit_name || ''} · ${money(product.amounts?.[recipient.to_pos_id])}` : undefined}
														onClick={() => value > 0 && openDetails({ toPosId: recipient.to_pos_id, toPosName: recipient.to_pos_name, product })}>
														<span style={{ fontWeight: isMax ? 800 : 400 }}>
															{value > 0 ? (cellMode === 'amount' ? money(value) : qty(value)) : '—'}
														</span>
													</td>
												)
											})}
											<td className="chain-num chain-total-col">
												{cellMode === 'amount' ? money(product.amount) : <>{qty(product.quantity)} <span className="chain-muted">{product.unit_name}</span></>}
											</td>
											<FillCell percent={product.share} tone="success">{percent(product.share)}</FillCell>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && cellMode === 'amount' && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										{recipients.map(recipient => (
											<td key={recipient.to_pos_id} className="chain-num chain-click"
												onClick={() => openDetails({ toPosId: recipient.to_pos_id, toPosName: recipient.to_pos_name })}>
												{money(recipient.amount)}
												<div className="chain-muted">{percent(recipient.share)}</div>
											</td>
										))}
										<td className="chain-num chain-total-col">{money(totals.amount)}</td>
										<td className="chain-num">100%</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_tr_products_hint')}
					</div>
				</div>
			</div>
		</>
	)
}

// Вкладка «По суммам»: рейтинг получателей, статусы, месяцы
function AmountsTab({ data, statusLabels, openDetails }) {
	const { t } = useTranslation()
	const recipients = useMemo(() => data?.recipients || [], [data])
	const totals = data?.totals || {}
	const statuses = data?.statuses || {}
	const months = data?.months || []

	const { sorted, sort, toggle } = useSort(recipients, 'amount')
	const { ranks, count } = useMemo(() => buildRanks(recipients, 'amount', true, 'to_pos_id'), [recipients])
	const maxAmount = useMemo(() => columnStats(recipients, 'amount').max, [recipients])
	const stats = useMemo(() => ({
		markup_percent: columnStats(recipients, 'markup_percent'),
		avg_document: columnStats(recipients, 'avg_document'),
	}), [recipients])
	const top = recipients[0]
	const shownStatuses = STATUS_GROUPS.filter(status => (statuses[status]?.documents || 0) > 0)

	return (
		<>
			<div className="row g-3 mb-3">
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_tr_amount')} icon="uil-truck" color="#5b73e8"
						value={money(totals.amount)}
						sub={<><Delta value={totals.amount_delta} /> {t('chain_vs_prev')} · {formatMoney(totals.documents)} {t('chain_tr_documents_lower')}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('markup')} icon="uil-chart-growth" color="#12b886"
						value={money(totals.markup)}
						sub={<>{percent(totals.markup_percent)} {t('chain_tr_to_cost')} · {t('cost_price')} {money(totals.cost)}</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_tr_status_pending')} icon="uil-clock" color="#f59f00"
						value={money(statuses.pending?.amount)}
						sub={<>
							{formatMoney(statuses.pending?.documents)} {t('chain_tr_documents_lower')}
							{(statuses.cancelled?.documents || 0) > 0 && <> · {t('chain_tr_status_cancelled')}: {money(statuses.cancelled.amount)}</>}
						</>} />
				</div>
				<div className="col-xl-3 col-md-6">
					<ChainKpi title={t('chain_tr_top_recipient')} icon="uil-trophy" color="#7c4dff"
						value={top ? top.to_pos_name : '—'}
						sub={top ? <>{money(top.amount)} · {percent(top.share)}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_tr_by_recipients')}</h5>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll chain-scroll-auto">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="to_pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_tr_recipient')}</SortTh>
									<SortTh sortKey="amount" sort={sort} onSort={toggle} className="text-end">{t('chain_tr_amount')}</SortTh>
									<SortTh sortKey="amount_delta" sort={sort} onSort={toggle} className="text-end" title={t('chain_vs_prev')}>{t('chain_growth')}</SortTh>
									<SortTh sortKey="cost" sort={sort} onSort={toggle} className="text-end">{t('cost_price')}</SortTh>
									<SortTh sortKey="markup_percent" sort={sort} onSort={toggle} className="text-end" title={t('chain_tr_markup_hint')}>{t('markup')}</SortTh>
									<SortTh sortKey="documents" sort={sort} onSort={toggle} className="text-end">{t('chain_tr_documents')}</SortTh>
									<SortTh sortKey="avg_document" sort={sort} onSort={toggle} className="text-end">{t('chain_tr_avg_document')}</SortTh>
									{shownStatuses.map(status => (
										<th key={status} className="text-center">{statusLabels[status]}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={8 + shownStatuses.length} />}
								{sorted.map(item => (
									<tr key={item.to_pos_id}>
										<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.to_pos_id]} count={count} /></td>
										<td className="chain-name chain-sticky-2">
											<span>{item.to_pos_name}</span>
											{item.to_login && <div className="chain-muted">{item.to_login}</div>}
										</td>
										<FillCell percent={maxAmount > 0 ? item.amount / maxAmount * 100 : 0} tone="primary"
											title={t('chain_open_details')}
											onClick={() => openDetails({ toPosId: item.to_pos_id, toPosName: item.to_pos_name })}>
											<b>{money(item.amount)}</b>
											<div className="chain-muted">{percent(item.share)}</div>
										</FillCell>
										<td className="chain-num">
											<Delta value={item.amount_delta} />
											<div className="chain-muted">{money(item.prev_amount)}</div>
										</td>
										<td className="chain-num">{money(item.cost)}</td>
										<td className="chain-num" style={heatStyle(item.markup_percent, stats.markup_percent)}>
											{money(item.markup)}
											<div className="chain-muted">{percent(item.markup_percent)}</div>
										</td>
										<td className="chain-num">{formatMoney(item.documents)}</td>
										<td className="chain-num">{money(item.avg_document)}</td>
										{shownStatuses.map(status => {
											const group = item.statuses?.[status] || {}
											return group.documents > 0 ? (
												<td key={status} className="chain-click" style={statusCellStyle(GROUP_COLOR[status])}
													title={t('chain_open_details')}
													onClick={() => openDetails({ toPosId: item.to_pos_id, toPosName: item.to_pos_name, status })}>
													{money(group.amount)}
													<div className="chain-muted fw-normal">{formatMoney(group.documents)} {t('chain_tr_documents_lower')}</div>
												</td>
											) : <td key={status} className="text-center chain-muted">—</td>
										})}
									</tr>
								))}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num chain-click" onClick={() => openDetails({})}>{money(totals.amount)}</td>
										<td className="chain-num"><Delta value={totals.amount_delta} /></td>
										<td className="chain-num">{money(totals.cost)}</td>
										<td className="chain-num">{money(totals.markup)}<div className="chain-muted">{percent(totals.markup_percent)}</div></td>
										<td className="chain-num">{formatMoney(totals.documents)}</td>
										<td className="chain-num">{money(totals.avg_document)}</td>
										{shownStatuses.map(status => (
											<td key={status} className="chain-click" style={statusCellStyle(GROUP_COLOR[status])} onClick={() => openDetails({ status })}>
												{money(statuses[status]?.amount)}
												<div className="chain-muted fw-normal">{formatMoney(statuses[status]?.documents)} {t('chain_tr_documents_lower')}</div>
											</td>
										))}
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_tr_amounts_hint')}
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				{months.length > 1 && (
					<div className="col-xl-7">
						<div className="card chain-card mb-0 h-100">
							<div className="card-body">
								<div className="chain-toolbar">
									<div className="chain-toolbar-group">
										<h5 className="mb-0 me-2">{t('chain_tr_by_months')}</h5>
									</div>
									<HeatLegend mode="success" />
								</div>
								<div className="chain-scroll chain-scroll-auto">
									<table className="chain-table">
										<thead>
											<tr>
												<th className="chain-sticky">{t('chain_tr_recipient')}</th>
												{months.map(month => <th key={month} className="text-end">{monthLabel(month)}</th>)}
												<th className="text-end chain-total-col">{t('total')}</th>
											</tr>
										</thead>
										<tbody>
											{recipients.map(item => {
												const rowMax = Math.max(0, ...item.months.map(value => Number(value || 0)))
												return (
													<tr key={item.to_pos_id}>
														<td className="chain-name chain-sticky">{item.to_pos_name}</td>
														{months.map((month, index) => {
															const value = Number(item.months[index] || 0)
															return (
																<td key={month} className={`chain-num ${value > 0 ? 'chain-click' : ''}`}
																	style={intensityStyle(value, rowMax, 'success')}
																	onClick={() => value > 0 && openDetails({ toPosId: item.to_pos_id, toPosName: item.to_pos_name, month })}>
																	{value > 0 ? money(value) : '—'}
																</td>
															)
														})}
														<td className="chain-num chain-total-col">{money(item.amount)}</td>
													</tr>
												)
											})}
										</tbody>
										{recipients.length > 0 && (
											<tfoot>
												<tr>
													<td className="chain-sticky">{t('total')}</td>
													{months.map((month, index) => (
														<td key={month} className="chain-num chain-click" onClick={() => openDetails({ month })}>{money(data.month_totals[index])}</td>
													))}
													<td className="chain-num chain-total-col">{money(totals.amount)}</td>
												</tr>
											</tfoot>
										)}
									</table>
								</div>
							</div>
						</div>
					</div>
				)}
				<div className={months.length > 1 ? 'col-xl-5' : 'col-12'}>
					<RecipientsChart recipients={recipients} statusLabels={statusLabels} />
				</div>
			</div>
		</>
	)
}

// Получатели стопкой по статусам: сколько принято, сколько висит на приёмке
function RecipientsChart({ recipients, statusLabels }) {
	const { t } = useTranslation()
	const series = STATUS_GROUPS
		.map(status => ({ name: statusLabels[status], status, data: recipients.map(item => Math.round(item.statuses?.[status]?.amount || 0)) }))
		.filter(item => item.data.some(value => value > 0))
	const options = {
		chart: { type: 'bar', stacked: true, toolbar: { show: false } },
		plotOptions: { bar: { horizontal: true, borderRadius: 2, barHeight: '62%' } },
		colors: series.map(item => `rgb(${STATUS_RGB[GROUP_COLOR[item.status]]})`),
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		xaxis: { categories: recipients.map(item => item.to_pos_name), labels: { formatter: value => formatMoney(Math.round(value / 1000000)) + 'M' } },
		tooltip: { y: { formatter: value => formatMoney(Math.round(value)) } },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_tr_by_recipients')}</h5></div>
				{series.length > 0
					? <ReactApexChart type="bar" height={Math.max(300, recipients.length * 40 + 90)} series={series} options={options} />
					: <div className="chain-empty">{t('chain_no_data')}</div>}
			</div>
		</div>
	)
}

export default Transfers
